import { useState } from 'react';
import { Trash2, Plus, ChevronLeft, Check, X } from 'lucide-react';

/* ---------- 世界书列表 ---------- */
export function LorebookList({ entries, onEdit, onDelete, onToggle, onNew, onClose }) {
  return (
    <>
      <label className="ch-settings-label">
        世界书条目 · <span className="ch-en">lorebook entries</span>
      </label>
      <p className="ch-settings-hint">
        每条都可以<b>独立启用</b>。最近 N 条消息里出现关键词，对应条目就会被注入到 system prompt。
      </p>

      {entries.length === 0 && (
        <div className="cc-empty">还没有条目 · 新建第一条</div>
      )}

      <div className="cc-list">
        {entries.map((e) => (
          <div
            key={e.id}
            className={'cc-card cc-lore-card' + (e.enabled ? ' on' : ' off')}
          >
            <button
              className="cc-card-main"
              onClick={() => onEdit(e.id)}
              title="点击编辑"
            >
              <div className="cc-card-meta">
                <div className="cc-lore-keys">
                  {e.keys.length > 0 ? (
                    e.keys.slice(0, 4).map((k, i) => (
                      <span key={i} className="cc-lore-key">{k}</span>
                    ))
                  ) : (
                    <span className="cc-lore-keys-empty">（无关键词）</span>
                  )}
                  {e.keys.length > 4 && (
                    <span className="cc-lore-keys-more">+{e.keys.length - 4}</span>
                  )}
                </div>
                <div className="cc-card-bio">
                  {e.content?.trim()
                    ? e.content.trim().slice(0, 50) +
                      (e.content.trim().length > 50 ? '…' : '')
                    : '（无内容）'}
                </div>
              </div>
            </button>
            <div className="cc-card-tools">
              <label
                className="cc-toggle"
                title={e.enabled ? '启用中（点击停用）' : '已停用（点击启用）'}
                onClick={(ev) => ev.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={e.enabled}
                  onChange={() => onToggle(e.id)}
                />
                <span className="cc-toggle-track">
                  <span className="cc-toggle-thumb" />
                </span>
              </label>
              <button
                className="cc-icon-btn cc-icon-danger"
                onClick={() => onDelete(e.id)}
                title="删除"
                aria-label="删除"
              >
                <Trash2 size={13} strokeWidth={1.6} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <button className="cc-add" onClick={onNew}>
        <Plus size={14} strokeWidth={1.8} /> 新建条目
      </button>

      <div className="ch-settings-actions">
        <button className="ch-tool-btn on" onClick={onClose}>
          完 成
        </button>
      </div>
    </>
  );
}

/* ---------- 世界书编辑器 ---------- */
export function LorebookEditor({ initial, onBack, onSave }) {
  const [draft, setDraft] = useState(initial);
  const [keyInput, setKeyInput] = useState('');

  function patch(p) {
    setDraft((d) => ({ ...d, ...p }));
  }

  function addKey() {
    const k = keyInput.trim();
    if (!k) return;
    if (draft.keys.includes(k)) {
      setKeyInput('');
      return;
    }
    patch({ keys: [...draft.keys, k] });
    setKeyInput('');
  }
  function removeKey(k) {
    patch({ keys: draft.keys.filter((x) => x !== k) });
  }
  function onKeyInputKeyDown(e) {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addKey();
    } else if (e.key === 'Backspace' && !keyInput && draft.keys.length > 0) {
      // 空状态退格 = 删最后一个 tag
      patch({ keys: draft.keys.slice(0, -1) });
    }
  }

  return (
    <>
      <div className="cc-editor-top">
        <button className="cc-back" onClick={onBack}>
          <ChevronLeft size={14} strokeWidth={1.6} /> 返回
        </button>
        <span className="cc-editor-title">编辑条目</span>
      </div>

      <label className="ch-settings-label cc-row-label">
        关键词 · <span className="ch-en">keys</span>
      </label>
      <div className="cc-tag-input">
        {draft.keys.map((k) => (
          <span key={k} className="cc-tag">
            {k}
            <button
              type="button"
              className="cc-tag-x"
              onClick={() => removeKey(k)}
              aria-label={`删除关键词 ${k}`}
            >
              <X size={11} strokeWidth={2} />
            </button>
          </span>
        ))}
        <input
          className="cc-tag-input-field"
          value={keyInput}
          onChange={(e) => setKeyInput(e.target.value)}
          onKeyDown={onKeyInputKeyDown}
          onBlur={addKey}
          placeholder={draft.keys.length === 0 ? '输入关键词回车添加…' : ''}
        />
      </div>

      <label className="ch-settings-label cc-row-label">
        内 容 · <span className="ch-en">content</span>
      </label>
      <textarea
        className="ch-settings-input"
        value={draft.content}
        onChange={(e) => patch({ content: e.target.value })}
        rows={6}
        placeholder={'命中任一关键词时，这段内容会被注入到 system prompt 的"相关背景"里。\n比如：lingke.bond 是铃和Sael共建的私人记忆空间……'}
      />

      <label className="ch-settings-label cc-row-label">
        优先级 · <span className="ch-en">priority</span>
        <span className="cc-slider-value">{draft.priority}</span>
      </label>
      <div className="cc-slider-wrap">
        <input
          type="range"
          min="0"
          max="1000"
          step="10"
          value={draft.priority}
          onChange={(e) => patch({ priority: parseInt(e.target.value, 10) })}
          className="cc-slider"
        />
        <div className="cc-slider-hint">
          <span>低</span>
          <span>多条命中时排前面</span>
        </div>
      </div>

      <label className="ch-settings-label cc-row-label">
        搜索深度 · <span className="ch-en">search depth</span>
        <span className="cc-slider-value">最近 {draft.search_depth} 条</span>
      </label>
      <div className="cc-slider-wrap">
        <input
          type="range"
          min="1"
          max="20"
          step="1"
          value={draft.search_depth}
          onChange={(e) => patch({ search_depth: parseInt(e.target.value, 10) })}
          className="cc-slider"
        />
        <div className="cc-slider-hint">
          <span>只看最新一句</span>
          <span>看远一点</span>
        </div>
      </div>

      <div className="cc-checkbox-row">
        <label className="cc-checkbox">
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(e) => patch({ enabled: e.target.checked })}
          />
          <span>启用</span>
        </label>
        <label className="cc-checkbox">
          <input
            type="checkbox"
            checked={draft.case_sensitive}
            onChange={(e) => patch({ case_sensitive: e.target.checked })}
          />
          <span>区分大小写</span>
        </label>
        <label className="cc-checkbox">
          <input
            type="checkbox"
            checked={draft.match_whole_word}
            onChange={(e) => patch({ match_whole_word: e.target.checked })}
          />
          <span>完整单词</span>
        </label>
      </div>

      <div className="ch-settings-actions">
        <button className="ch-tool-btn" onClick={onBack}>
          取 消
        </button>
        <button
          className="ch-tool-btn on"
          onClick={() => onSave(draft)}
          disabled={draft.keys.length === 0}
          title={draft.keys.length === 0 ? '至少要有一个关键词' : ''}
        >
          <Check size={13} strokeWidth={1.8} /> 保 存
        </button>
      </div>
    </>
  );
}
